"use client"

import * as React from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { Eye, EyeOff } from "lucide-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"

import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { FormError } from "@/components/signup/form-error"
import { ApiError } from "@/lib/api/client"
import { useAuth } from "@/lib/auth/auth-context"
import { loginSchema, type LoginValues } from "@/lib/login/schemas"
import * as m from "@/paraglide/messages"

interface LoginFormProps {
  /** When arriving from an invitation link: after login, return to the landing
   * page so acceptance (and its error handling) happens in one place. */
  invitationToken?: string
  /** Company name from the invitation preview; renders the "join X" banner. */
  invitationCompanyName?: string
  /**
   * Where to return after login when there's no invitation flow -- e.g. a
   * finalize-notification email's project deep link. Already validated
   * same-site by the /login page itself.
   */
  redirectTo?: string
}

export function LoginForm({ invitationToken, invitationCompanyName, redirectTo }: LoginFormProps) {
  const router = useRouter()
  const { login } = useAuth()
  const [serverErrors, setServerErrors] = React.useState<string[]>([])
  const [showPassword, setShowPassword] = React.useState(false)

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    setServerErrors([])
    try {
      await login(values)
      router.push(
        invitationToken
          ? `/invitations/${encodeURIComponent(invitationToken)}`
          : (redirectTo ?? "/"),
      )
    } catch (error) {
      if (error instanceof ApiError) {
        setServerErrors(error.errors)
      } else {
        setServerErrors([m.login_error_generic()])
      }
    }
  })

  return (
    <div className="grid gap-6">
      {invitationCompanyName ? (
        <div
          role="status"
          className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm"
        >
          {m.login_invitation_banner({ company: invitationCompanyName })}
        </div>
      ) : null}

      <Form {...form}>
        <form onSubmit={onSubmit} className="grid gap-4" noValidate>
          <FormError errors={serverErrors} />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{m.login_email_label()}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={m.login_email_placeholder()}
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{m.login_password_label()}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      className="pr-10"
                      {...field}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={
                        showPassword
                          ? m.login_password_hide()
                          : m.login_password_show()
                      }
                      className="absolute inset-y-0 right-0 flex items-center px-2.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 rounded-r-lg"
                    >
                      {showPassword ? (
                        <EyeOff className="size-4" />
                      ) : (
                        <Eye className="size-4" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            size="lg"
            className="mt-2 w-full"
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting
              ? m.login_button_submitting()
              : m.login_button_submit()}
          </Button>
        </form>
      </Form>

      <a href="/forgot-password" className="-mt-3 text-center text-sm font-medium text-primary underline-offset-4 hover:underline">
        {m.password_forgot_link()}
      </a>

      <p className="text-center text-sm text-muted-foreground">
        {m.login_signup_prompt()}{" "}
        <Link href="/signup" className="font-medium text-primary underline-offset-4 hover:underline">
          {m.login_signup_link()}
        </Link>
      </p>
    </div>
  )
}
